import React from 'react';
import { format } from 'date-fns';
import { Message } from '../types';
import './MessageBubble.css';

interface MessageBubbleProps {
  message: Message;
  showSender: boolean;
  highlightText?: string;
}

// Memoized for performance - only re-render if props change
export const MessageBubble = React.memo<MessageBubbleProps>(({
  message,
  showSender,
  highlightText,
}) => {
  const timeStr = message.unixMs
    ? format(new Date(message.unixMs), 'h:mm a')
    : '';

  const highlightMessage = (text: string, highlight?: string) => {
    if (!highlight) return text;

    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <mark key={i} className="highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Generate avatar initials from sender name
  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Generate consistent color from sender name
  const getAvatarColor = (name: string) => {
    const colors = [
      '#1abc9c', '#3498db', '#9b59b6', '#e74c3c', 
      '#f39c12', '#16a085', '#27ae60', '#2980b9',
      '#8e44ad', '#d35400', '#c0392b', '#7f8c8d'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className="message-wrapper">
      {!message.isFromMe && showSender && message.sender && (
        <div 
          className="message-avatar" 
          style={{ backgroundColor: getAvatarColor(message.sender) }}
        >
          {getInitials(message.sender)}
        </div>
      )}
      {!message.isFromMe && !showSender && (
        <div className="message-avatar-spacer"></div>
      )}
      
      <div
        className={`message-bubble ${message.isFromMe ? 'from-me' : 'from-other'}`}
      >
        {showSender && !message.isFromMe && message.sender && (
          <div className="message-sender">{message.sender}</div>
        )}
        
        <div className="message-content">
          {highlightMessage(message.text, highlightText)}
        </div>
        
        <div className="message-time">{timeStr}</div>
        
        {message.parseWarnings && message.parseWarnings.length > 0 && (
          <div className="message-warning" title={message.parseWarnings.join(', ')}>
            ⚠️
          </div>
        )}
      </div>

      {message.isFromMe && showSender && (
        <div 
          className="message-avatar" 
          style={{ backgroundColor: '#25d366' }}
        >
          Me
        </div>
      )}
      {message.isFromMe && !showSender && (
        <div className="message-avatar-spacer"></div>
      )}
    </div>
  );
});
